import * as React from 'react';
import { FC, Fragment } from 'react';

import Alert from 'components/alert/alert';
import AppDownloadSection from 'components/features/components/app-download-section';
import ContentCards from 'components/features/components/braze-content-cards';
import CarouselSection from 'components/features/components/carousel-section';
import FanFavoritesSection from 'components/features/components/fan-favorites';
import { HeroContainer } from 'components/features/components/hero';
import { MainHeroContainer } from 'components/features/components/main-hero/main-hero-container';
import { MarketingTileGroup } from 'components/features/components/marketing-tile-group';
import { OfferCarouselSection } from 'components/features/components/offer-carousel-section';
import OurServicesSection from 'components/features/components/our-services';
import { RecentItemsSection } from 'components/features/components/recent-items';
import { RewardsSection } from 'components/features/components/rewards-section';
import Trending from 'components/features/components/trending-section';
import { IMarketingCardGroupFragment, IMarketingTileGroup } from 'generated/sanity-graphql';

import { Section } from './home-page/styles';
import { Components } from './home-page/types';

export const Features: FC<{ components: Components; location: string }> = ({
  components,
  location,
}) => {
  if (!components) {
    return null;
  }
  return (
    <>
      {components.map((item, idx) => {
        if (!item) {
          return null;
        }
        const first = idx === 0;
        const last = idx + 1 === (components?.length || 0);

        //TODO ICFE-425 - Determine the need of React.lazy for features.tsx component
        switch (item.__typename) {
          case 'MainHero': {
            return <MainHeroContainer key={`${location}_MainHero_${item._id}`} item={item} />;
          }

          case 'Hero': {
            return (
              <Fragment key={`${location}_Hero_${item._id}`}>
                <HeroContainer hero={item} />
              </Fragment>
            );
          }

          case 'BrazeContentCards': {
            return (
              <Fragment key={`${location}_BrazeContentCards_${item._id}`}>
                <ContentCards item={item} />
              </Fragment>
            );
          }

          case 'Alert': {
            return (
              <Fragment key={`${location}_Alert_${item._id}`}>
                <Alert
                  headline={item.heading?.locale || ''}
                  body={item.body?.locale || ''}
                  learnMore={item.learnMoreText?.locale || ''}
                  learnMoreUrl={item.learnMoreUrl?.locale || ''}
                />
              </Fragment>
            );
          }

          case 'MarketingTileCarousel': {
            return (
              <Fragment key={`${location}_MarketingTileCarousel_${item._id}`}>
                <CarouselSection item={item} />
              </Fragment>
            );
          }

          case 'MarketingCardGroup':
            return (
              <Section
                key={`${location}_MarketingCardGroup_${item._id}`}
                first={false}
                last={last}
                shouldRenderDefaultBackground={true}
              >
                <MarketingTileGroup item={item as unknown as IMarketingCardGroupFragment} />
              </Section>
            );

          case 'MarketingTileGroup':
            return (
              <Section key={`${location}_MarketingTileGroup_${item._id}`} first={first} last={last}>
                <MarketingTileGroup item={item as IMarketingTileGroup} />
              </Section>
            );

          case 'Trending': {
            return (
              <Fragment key={`${location}_Trending_${item._id}`}>
                <Trending item={item} />
              </Fragment>
            );
          }

          case 'FanFavorites': {
            return (
              <Fragment key={`${location}_FanFavorites_${item._id}`}>
                <FanFavoritesSection item={item} />
              </Fragment>
            );
          }

          case 'OurServices': {
            return (
              <Fragment key={`${location}_OurServices_${item._id}`}>
                <OurServicesSection item={item} />
              </Fragment>
            );
          }

          case 'AppDownload': {
            return (
              <Fragment key={`${location}_AppDownload_${item._id}`}>
                <AppDownloadSection item={item?.details} />
              </Fragment>
            );
          }

          case 'RecentItems': {
            return (
              <Fragment key={`${location}_RecentItems_${item._id}`}>
                <RecentItemsSection />
              </Fragment>
            );
          }

          case 'RewardsSection': {
            return (
              <Fragment key={`${location}_RewardsSection_${item._id}`}>
                <RewardsSection item={item} />
              </Fragment>
            );
          }

          case 'OfferCarouselSection': {
            return (
              <Fragment key={`${location}_OfferCarouselSection_${item._id}`}>
                <OfferCarouselSection
                  title={item.offerCarouselTitle}
                  offers={item.offerCarouselList}
                />
              </Fragment>
            );
          }

          default:
            return null;
        }
      })}
    </>
  );
};
